/**
 * Phase 2J-C — Commission Batch Payout Runs admin UI.
 *
 * Drops into the existing /admin/commissions page below the ledger.
 * The flow is:
 *   1. Admin clicks "Generate this month's payout run"
 *   2. The run is created in draft state with one line per partner
 *   3. Admin reviews each partner line (count + subtotal + statement PDF)
 *   4. Admin clicks "Mark paid" per partner — underlying commissions
 *      transition to `paid` via the existing commission_service.transition
 *
 * No money moves. No emails are sent. Every step requires admin click.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  CalendarDays, ChevronDown, ChevronRight, FileDown, Loader2,
  Plus, Trash2,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const LINE_PILL = {
  pending:  { bg: "bg-amber-100",   text: "text-amber-800",   label: "Pending" },
  paid:     { bg: "bg-emerald-100", text: "text-emerald-800", label: "Paid"    },
  excluded: { bg: "bg-slate-200",   text: "text-slate-700",   label: "Excluded"},
};

const fmt = (n) => `$${(n || 0).toFixed(2)}`;

export default function PayoutRunsPanel() {
  const [runs, setRuns] = useState([]);
  const [period, setPeriod] = useState({ start: "", end: "" });
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/commissions/payout-runs");
      setRuns(data.runs || []);
    } catch (e) { /* silent — page-level UI hides this panel if it 403s */ }
  };

  useEffect(() => {
    refresh();
    // Pre-fill the period range with the current month
    api.get("/admin/commissions/payout-runs/current-period")
       .then(({ data }) => setPeriod({ start: data.period_start, end: data.period_end }))
       .catch(() => {});
  }, []);

  const generate = async () => {
    if (!period.start || !period.end) {
      toast.error("Please choose a period range first");
      return;
    }
    if (!window.confirm(
      `Generate a payout run for ${period.start} to ${period.end}? ` +
      `This bundles every APPROVED commission in the window into one ` +
      `consolidated payout per partner. No money is moved.`,
    )) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/commissions/payout-runs", {
        period_start: period.start, period_end: period.end,
      });
      toast.success(`Run created · ${data.total_partners} partners · ${fmt(data.grand_total)}`);
      setExpanded(data.id);
      refresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "object" && detail?.error === "already_exists") {
        toast.error(`A run already exists for this period (run ${detail.existing_run_id.slice(0, 8)})`);
        setExpanded(detail.existing_run_id);
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to generate run");
      }
    } finally {
      setGenerating(false);
    }
  };

  const markPaid = async (runId, partnerId, businessName, amount) => {
    if (!window.confirm(
      `Mark ${businessName} paid (${fmt(amount)})? ` +
      `This will transition every bundled commission to PAID. ` +
      `No money is moved automatically.`,
    )) return;
    setBusy(true);
    try {
      await api.post(
        `/admin/commissions/payout-runs/${runId}/lines/${partnerId}/mark-paid`,
        { notes: "" },
      );
      toast.success("Partner marked paid");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mark paid failed");
    } finally { setBusy(false); }
  };

  const exclude = async (runId, partnerId, businessName) => {
    const reason = window.prompt(
      `Exclude ${businessName} from this run? ` +
      `Their commissions remain APPROVED for the next run.\n\n` +
      `Optional reason:`,
      "",
    );
    if (reason === null) return;  // cancelled
    setBusy(true);
    try {
      await api.post(
        `/admin/commissions/payout-runs/${runId}/lines/${partnerId}/exclude`,
        { reason },
      );
      toast.success("Partner excluded from run");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Exclude failed");
    } finally { setBusy(false); }
  };

  const removeRun = async (runId, monthLabel) => {
    if (!window.confirm(
      `Delete the draft payout run for ${monthLabel}? ` +
      `Refused if any partner has already been marked paid.`,
    )) return;
    setBusy(true);
    try {
      await api.delete(`/admin/commissions/payout-runs/${runId}`);
      toast.success("Run deleted");
      if (expanded === runId) setExpanded(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  const dlStatement = (runId, partnerId, businessName) => {
    // Open in a new tab — bearer token is on api defaults, but PDFs need an
    // axios call with responseType blob to forward credentials.
    api.get(
      `/admin/commissions/payout-runs/${runId}/lines/${partnerId}/statement.pdf`,
      { responseType: "blob" },
    ).then(({ data }) => {
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const win = window.open(url, "_blank", "noopener");
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.download = `payout-${businessName || partnerId}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    }).catch(() => toast.error("Statement PDF failed"));
  };

  const expandedRun = useMemo(
    () => runs.find((r) => r.id === expanded),
    [runs, expanded],
  );

  return (
    <section data-testid="payout-runs-panel" className="mt-10">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-emerald-600">Batch payouts</div>
          <h2 className="font-display mt-1 text-2xl font-bold text-slate-900">Payout runs</h2>
          <p className="text-slate-500 mt-1 text-xs max-w-2xl">
            Bundle every approved (unpaid) commission for a given period into one consolidated payout per partner.
            Review the totals, download each partner's statement PDF, and confirm payment manually. No money moves automatically.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Period start</label>
            <input
              type="date"
              data-testid="payout-period-start"
              value={period.start}
              onChange={(e) => setPeriod({ ...period, start: e.target.value })}
              className="block rounded-full border-slate-200 text-xs px-3 py-1.5 mt-1"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Period end</label>
            <input
              type="date"
              data-testid="payout-period-end"
              value={period.end}
              onChange={(e) => setPeriod({ ...period, end: e.target.value })}
              className="block rounded-full border-slate-200 text-xs px-3 py-1.5 mt-1"
            />
          </div>
          <Button
            data-testid="generate-payout-run-btn"
            onClick={generate}
            disabled={generating}
            size="sm"
            className="rounded-full bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-semibold"
          >
            {generating
              ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Generating…</>
              : <><Plus className="w-3.5 h-3.5 mr-1.5" />Generate payout run</>}
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden">
        {runs.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <CalendarDays className="w-7 h-7 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">No payout runs yet. Generate one using the dates above.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {runs.map((r) => {
              const isOpen = expanded === r.id;
              const paidCount = (r.partner_lines || []).filter((l) => l.status === "paid").length;
              return (
                <li key={r.id} data-testid={`payout-run-${r.id}`}>
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : r.id)}
                    className="w-full text-left px-5 py-4 flex items-center gap-3 hover:bg-slate-50 transition"
                  >
                    {isOpen
                      ? <ChevronDown className="w-4 h-4 text-slate-400" />
                      : <ChevronRight className="w-4 h-4 text-slate-400" />}
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900">{r.month_label}</div>
                      <div className="text-[11px] text-slate-500">
                        {r.total_partners} partner{r.total_partners !== 1 ? "s" : ""} · {r.total_commissions} commission{r.total_commissions !== 1 ? "s" : ""} · {paidCount}/{r.total_partners} paid
                      </div>
                    </div>
                    <div className="font-mono font-bold text-emerald-700">{fmt(r.grand_total)}</div>
                  </button>
                  {isOpen && (
                    <div className="bg-slate-50/70 px-5 py-3 border-t border-slate-100">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                            <th className="text-left py-2">Partner</th>
                            <th className="text-right py-2">Count</th>
                            <th className="text-right py-2">Subtotal</th>
                            <th className="text-left py-2 pl-3">Status</th>
                            <th className="text-right py-2">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(r.partner_lines || []).map((line) => {
                            const pill = LINE_PILL[line.status] || LINE_PILL.pending;
                            const name = line.partner_business_name || line.partner_email || line.partner_id;
                            return (
                              <tr
                                key={line.partner_id}
                                data-testid={`payout-line-${line.partner_id}`}
                                className="border-t border-slate-200"
                              >
                                <td className="py-2.5">
                                  <div className="font-medium text-slate-900">{name}</div>
                                  <div className="text-[10px] text-slate-500">{line.partner_email}</div>
                                </td>
                                <td className="py-2.5 text-right text-slate-700">{line.count}</td>
                                <td className="py-2.5 text-right font-mono font-bold text-emerald-700">{fmt(line.subtotal_amount)}</td>
                                <td className="py-2.5 pl-3">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${pill.bg} ${pill.text}`}>
                                    {pill.label}
                                  </span>
                                </td>
                                <td className="py-2.5 text-right">
                                  <div className="inline-flex gap-1 items-center">
                                    <button
                                      data-testid={`payout-stmt-${line.partner_id}`}
                                      onClick={() => dlStatement(r.id, line.partner_id, name)}
                                      title="Statement PDF"
                                      className="p-1.5 rounded text-slate-500 hover:bg-slate-200"
                                    >
                                      <FileDown className="w-3.5 h-3.5" />
                                    </button>
                                    {line.status === "pending" && (
                                      <>
                                        <button
                                          data-testid={`payout-mark-paid-${line.partner_id}`}
                                          onClick={() => markPaid(r.id, line.partner_id, name, line.subtotal_amount)}
                                          disabled={busy}
                                          className="px-2 py-1 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-bold disabled:opacity-50"
                                        >
                                          MARK PAID
                                        </button>
                                        <button
                                          data-testid={`payout-exclude-${line.partner_id}`}
                                          onClick={() => exclude(r.id, line.partner_id, name)}
                                          disabled={busy}
                                          title="Exclude from this run"
                                          className="p-1.5 rounded text-rose-500 hover:bg-rose-50 disabled:opacity-50"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {paidCount === 0 && (
                        <div className="flex justify-end mt-3">
                          <Button
                            data-testid={`payout-delete-${r.id}`}
                            onClick={() => removeRun(r.id, r.month_label)}
                            disabled={busy}
                            size="sm"
                            variant="outline"
                            className="rounded-full text-rose-600 border-rose-200 hover:bg-rose-50 text-xs"
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                            Delete draft run
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
